/* ==========================================================================
   Product Updates Page Component (100% Multilingual & 9-Item Pagination)
   ========================================================================== */

import { t, getLocale } from '../locales/i18n.js';
import { openArticleModal } from '../components/ArticleModal.js';
import { getArticlesByCategory, subscribeArticleStore, parseArticleDate, formatArticleDate } from '../services/articleStore.js';
import { articleIndexTranslations } from '../locales/articleTranslationsData.js';


const productUpdatesUi = {
  en: {
    breadcrumbHome: 'Home',
    breadcrumbNews: 'News',
    breadcrumbCurrent: 'Product Updates',
    eyebrow: 'PRODUCT RELEASES & FIRMWARE',
    title: 'Product & Firmware Updates',
    subtitle: 'Explore the latest feature releases, neural acoustic model updates, camera firmware patches, and VMS integration plugins for Sound Event Detector.',
    countBadge: (cur, tot, p, tp) => `Showing ${cur} of ${tot} product updates (Page ${p} of ${tp})`,
    readBtn: 'Read Update Details',
    prev: '← Prev',
    next: 'Next →',
    defaultDate: 'Official Release'
  },
  cs: {
    breadcrumbHome: 'Domů',
    breadcrumbNews: 'Novinky',
    breadcrumbCurrent: 'Aktualizace produktů',
    eyebrow: 'PRODUKTOVÉ AKTUALIZACE A FIRMWARE',
    title: 'Produktové novinky a aktualizace firmwaru',
    subtitle: 'Prozkoumejte nejnovější verze funkcí, aktualizace neuronových modelů, firmware pro bezpečnostní kamery a integrační pluginy VMS pro Sound Event Detector.',
    countBadge: (cur, tot, p, tp) => `Zobrazeno ${cur} z ${tot} aktualizací (Strana ${p} z ${tp})`,
    readBtn: 'Číst detail aktualizace',
    prev: '← Předchozí',
    next: 'Další →',
    defaultDate: 'Oficiální vydání'
  },
  de: {
    breadcrumbHome: 'Startseite',
    breadcrumbNews: 'Nachrichten',
    breadcrumbCurrent: 'Produkt-Updates',
    eyebrow: 'PRODUKT-RELEASES & FIRMWARE',
    title: 'Produkt- & Firmware-Updates',
    subtitle: 'Entdecken Sie die neuesten Feature-Releases, neuronale Modell-Updates, Kamera-Firmware-Patches und VMS-Integrations-Plugins für den Sound Event Detector.',
    countBadge: (cur, tot, p, tp) => `Zeige ${cur} von ${tot} Updates (Seite ${p} von ${tp})`,
    readBtn: 'Update-Details lesen',
    prev: '← Zurück',
    next: 'Weiter →',
    defaultDate: 'Offizielles Release'
  },
  es: {
    breadcrumbHome: 'Inicio',
    breadcrumbNews: 'Noticias',
    breadcrumbCurrent: 'Actualizaciones de producto',
    eyebrow: 'LANZAMIENTOS DE PRODUCTO Y FIRMWARE',
    title: 'Actualizaciones de Producto y Firmware',
    subtitle: 'Explore los últimos lanzamientos de funciones, actualizaciones de modelos neuronales, parches de firmware y plugins VMS para Sound Event Detector.',
    countBadge: (cur, tot, p, tp) => `Mostrando ${cur} de ${tot} actualizaciones (Página ${p} de ${tp})`,
    readBtn: 'Leer detalle de actualización',
    prev: '← Anterior',
    next: 'Siguiente →',
    defaultDate: 'Lanzamiento oficial'
  },
  it: {
    breadcrumbHome: 'Home',
    breadcrumbNews: 'Notizie',
    breadcrumbCurrent: 'Aggiornamenti di prodotto',
    eyebrow: 'RELEASE DI PRODOTTO E FIRMWARE',
    title: 'Aggiornamenti di Prodotto e Firmware',
    subtitle: 'Esplora le release più recenti di funzionalità, aggiornamenti dei modelli neurali, firmware per telecamere e plugin VMS per Sound Event Detector.',
    countBadge: (cur, tot, p, tp) => `Visualizzazione di ${cur} su ${tot} aggiornamenti (Pagina ${p} di ${tp})`,
    readBtn: 'Leggi dettagli aggiornamento',
    prev: '← Precedente',
    next: 'Successivo →',
    defaultDate: 'Release ufficiale'
  },
  pt: {
    breadcrumbHome: 'Início',
    breadcrumbNews: 'Notícias',
    breadcrumbCurrent: 'Atualizações de produto',
    eyebrow: 'LANÇAMENTOS DE PRODUTO E FIRMWARE',
    title: 'Atualizações de Produto e Firmware',
    subtitle: 'Explore os lançamentos mais recentes de recursos, atualizações de modelos neurais, firmware para câmeras e plugins VMS para o Sound Event Detector.',
    countBadge: (cur, tot, p, tp) => `Exibindo ${cur} de ${tot} atualizações (Página ${p} de ${tp})`,
    readBtn: 'Ler detalhes da atualização',
    prev: '← Anterior',
    next: 'Próximo →',
    defaultDate: 'Lançamento oficial'
  },
  ar: {
    breadcrumbHome: 'الرئيسية',
    breadcrumbNews: 'الأخبار',
    breadcrumbCurrent: 'تحديثات المنتجات',
    eyebrow: 'إصدارات المنتجات والبرامج الثابتة',
    title: 'تحديثات المنتجات والبرمجيات',
    subtitle: 'استكشف أحدث الميزات، وتحديثات النماذج العصبية، وترقيات البرامج الثابتة للكاميرات، وإضافات التكامل مع أنظمة VMS لكاشف الأصوات SED.',
    countBadge: (cur, tot, p, tp) => `عرض ${cur} من أصل ${tot} تحديثاً (صفحة ${p} من ${tp})`,
    readBtn: 'قراءة تفاصيل التحديث',
    prev: '← السابق',
    next: 'التالي ←',
    defaultDate: 'إصدار رسمي'
  }
};

export function renderProductUpdatesPage(onNavigate) {
  const container = document.createElement('div');
  container.className = 'sed-page';
  const lang = getLocale() || 'en';
  const ui = productUpdatesUi[lang] || productUpdatesUi.en;

  const buildProductUpdatesList = () => {
    const rawList = getArticlesByCategory('product-updates');
    const seenSlugs = new Set();
    const mapped = [];

    for (const data of rawList) {
      if (!data) continue;
      const safeSlug = data.slug || 'product-update';
      if (seenSlugs.has(safeSlug)) continue;
      seenSlugs.add(safeSlug);

      let defaultCat = "Product Update";
      if (safeSlug.includes('app') || safeSlug.includes('mobile')) defaultCat = "Software & App";
      else if (safeSlug.includes('drone')) defaultCat = "Drone Classifier";
      else if (safeSlug.includes('camera') || safeSlug.includes('edge')) defaultCat = "Edge AI Firmware";
      else if (safeSlug.includes('graffiti')) defaultCat = "Acoustic Classifier";
      else if (safeSlug.includes('iso')) defaultCat = "Certification";
      else if (safeSlug.includes('max')) defaultCat = "Autonomous Vehicle AI";
      else if (safeSlug.includes('offer')) defaultCat = "Commercial License";

      const trans = (data.translations && data.translations[lang]) || (articleIndexTranslations[lang] && articleIndexTranslations[lang][safeSlug]) || {};
      const title = trans.title || data.title || safeSlug.replace(/-/g, ' ').toUpperCase();
      const category = trans.category || defaultCat;
      const desc = trans.excerpt || data.excerpt || (data.content ? data.content.replace(/<[^>]+>/g, '').slice(0, 160) + '...' : 'Sound Event Detector product release update.');

      mapped.push({
        id: safeSlug,
        title: title,
        category: category,
        date: trans.date || formatArticleDate(data.date, lang) || ui.defaultDate,
        rawDate: data.date,
        img: data.img || '/images/2.png',
        desc: desc,
        yt: data.yt || ''
      });
    }

    // Guaranteed strict descending sort by date (newest first)
    mapped.sort((a, b) => {
      const timeA = parseArticleDate(a.rawDate || a.date);
      const timeB = parseArticleDate(b.rawDate || b.date);
      return timeB - timeA;
    });

    return mapped;
  };

  let productUpdatesList = buildProductUpdatesList();

  const ITEMS_PER_PAGE = 9;
  let currentPage = 1;

  container.innerHTML = `
    <!-- Header Hero -->
    <section class="sed-section" style="background: linear-gradient(165deg, var(--navy-dark) 0%, var(--blue-dark) 100%); color: #ffffff; padding: 4.5rem 0 3.5rem;">
      <div class="sed-container">
        <div style="display: flex; gap: 0.5rem; align-items: center; font-size: 0.85rem; color: #cbd5e1; margin-bottom: 1.5rem; flex-wrap: wrap;">
          <a href="#home" style="color: #ffffff; text-decoration: none;" data-route="home">${ui.breadcrumbHome}</a> /
          <a href="#news" style="color: #ffffff; text-decoration: none;" data-route="news">${ui.breadcrumbNews}</a> /
          <span style="color: var(--blue-primary); font-weight: 700;">${ui.breadcrumbCurrent}</span>
        </div>
        <div class="sed-section-header" style="max-width: 840px; text-align: left; margin: 0;">
          <div class="sed-eyebrow" style="background: rgba(255,255,255,0.1); color: var(--blue-primary); border-color: rgba(255,255,255,0.2);">${ui.eyebrow}</div>
          <h1 class="sed-page-hero-title" style="color: #ffffff;">${ui.title}</h1>
          <p style="font-size: 1.15rem; color: #e2e8f0; line-height: 1.65; margin-bottom: 0;">${ui.subtitle}</p>
        </div>
      </div>
    </section>

    <!-- Product Updates Grid (Max 9 Items Per Page) -->
    <section class="sed-section" style="background-color: #ffffff; padding: 3.5rem 0 5rem;">
      <div class="sed-container">
        <div style="font-size: 0.88rem; font-weight: 700; color: var(--text-muted); margin-bottom: 1.5rem;" id="product-updates-count-badge">
          ${lang === 'cs' ? `Zobrazeno ${productUpdatesList.length} produktových aktualizací` : `Showing ${productUpdatesList.length} product updates`}
        </div>

        <div id="sed-product-grid" class="sed-grid-3">
          <!-- Paginated Product Updates -->
        </div>

        <!-- Pagination Controls -->
        <div id="sed-product-pagination" style="display: flex; justify-content: center; align-items: center; gap: 0.5rem; margin-top: 2.5rem; flex-wrap: wrap;">
          <!-- Page Buttons -->
        </div>
      </div>
    </section>
  `;

  function renderPage(page) {
    const gridEl = container.querySelector('#sed-product-grid');
    const pagEl = container.querySelector('#sed-product-pagination');
    const badgeEl = container.querySelector('#product-updates-count-badge');
    if (!gridEl) return;

    const totalPages = Math.ceil(productUpdatesList.length / ITEMS_PER_PAGE) || 1;
    if (page < 1) page = 1;
    if (page > totalPages) page = totalPages;
    currentPage = page;

    const start = (currentPage - 1) * ITEMS_PER_PAGE;
    const items = productUpdatesList.slice(start, start + ITEMS_PER_PAGE);

    if (badgeEl) {
      badgeEl.innerText = ui.countBadge(items.length, productUpdatesList.length, currentPage, totalPages);
    }

    const btnReadText = ui.readBtn;

    gridEl.innerHTML = items.map(pu => `
      <div class="sed-card" style="padding: 0; overflow: hidden; cursor: pointer; display: flex; flex-direction: column; justify-content: space-between;" data-product-id="${pu.id}">
        <div>
          <div style="height: 180px; overflow: hidden; background-color: var(--navy-dark); position: relative;">
            <img src="${pu.img}" alt="${pu.title}" style="width: 100%; height: 100%; object-fit: cover;" onerror="this.onerror=null; this.src='/images/SED_Novinky-aplikace_EN-2.png';">
            <div style="position: absolute; top: 12px; left: 12px; background: rgba(53, 168, 223, 0.9); color: #ffffff; padding: 0.25rem 0.65rem; border-radius: var(--radius-badge); font-size: 0.72rem; font-weight: 800;">
              ${pu.category}
            </div>
            ${pu.date ? `
              <div style="position: absolute; bottom: 12px; right: 12px; background: rgba(10, 25, 47, 0.85); color: #ffffff; padding: 0.2rem 0.6rem; border-radius: 4px; font-size: 0.72rem; font-weight: 700; backdrop-filter: blur(4px);">
                ${pu.date}
              </div>
            ` : ''}
          </div>
          <div style="padding: 1.5rem 1.5rem 1rem;">
            <h3 class="sed-card-title" style="word-break: break-word;">${pu.title}</h3>
            <p class="sed-card-text" style="font-size: 0.88rem; line-height: 1.6;">${pu.desc}</p>
          </div>
        </div>
        <div style="padding: 0 1.5rem 1.5rem; display: flex; gap: 0.5rem; align-items: center;">
          <button class="sed-btn sed-btn-primary btn-card-read" style="flex: 1; font-size: 0.85rem;">${btnReadText} ${pu.yt ? '🎬' : '→'}</button>
          <button class="sed-btn sed-btn-outline btn-card-share" data-share-url="https://www.jalud.eu/product-updates/${pu.id}" style="padding: 0.55rem 0.75rem; font-size: 0.85rem; color: var(--navy-dark); border-color: var(--slate-line);" title="Copy direct link / Kopírovat odkaz">🔗</button>
        </div>
      </div>
    `).join('');

    gridEl.querySelectorAll('[data-product-id]').forEach(card => {
      card.addEventListener('click', (e) => {
        if (e.target.closest('.btn-card-share')) return;
        const prodId = card.getAttribute('data-product-id');
        openArticleModal(prodId);
      });
    });

    gridEl.querySelectorAll('.btn-card-share').forEach(btn => {
      btn.addEventListener('click', (e) => {
        e.stopPropagation();
        const url = btn.getAttribute('data-share-url');
        navigator.clipboard.writeText(url).then(() => {
          const orig = btn.innerText;
          btn.innerText = '✓';
          btn.style.borderColor = '#10b981';
          btn.style.color = '#10b981';
          setTimeout(() => {
            btn.innerText = orig;
            btn.style.borderColor = '';
            btn.style.color = '';
          }, 2000);
        }).catch(() => {
          prompt('Direct link:', url);
        });
      });
    });

    if (pagEl) {
      if (totalPages <= 1) {
        pagEl.innerHTML = '';
      } else {
        const prevText = ui.prev;
        const nextText = ui.next;
        let pagHtml = `
          <button class="sed-btn sed-btn-outline btn-prod-page" data-page="${currentPage - 1}" ${currentPage === 1 ? 'disabled style="opacity:0.4; cursor:not-allowed;"' : ''}>${prevText}</button>
        `;
        for (let i = 1; i <= totalPages; i++) {
          const active = i === currentPage;
          pagHtml += `
            <button class="sed-btn ${active ? 'sed-btn-primary' : 'sed-btn-outline'} btn-prod-page" data-page="${i}" style="${active ? '' : 'color: var(--navy-dark); border-color: var(--slate-line);'} font-weight: 700; padding: 0.4rem 0.85rem;">${i}</button>
          `;
        }
        pagHtml += `
          <button class="sed-btn sed-btn-outline btn-prod-page" data-page="${currentPage + 1}" ${currentPage === totalPages ? 'disabled style="opacity:0.4; cursor:not-allowed;"' : ''}>${nextText}</button>
        `;
        pagEl.innerHTML = pagHtml;

        pagEl.querySelectorAll('.btn-prod-page').forEach(btn => {
          btn.addEventListener('click', () => {
            const p = parseInt(btn.getAttribute('data-page'), 10);
            if (!isNaN(p) && p >= 1 && p <= totalPages) {
              renderPage(p);
              const b = container.querySelector('#product-updates-count-badge');
              if (b && b.offsetTop) {
                window.scrollTo({ top: b.offsetTop - 100, behavior: 'smooth' });
              }
            }
          });
        });
      }
    }
  }

  // Initial render
  renderPage(1);

  // Subscribe to store updates (e.g., when articles sync from REST API)
  subscribeArticleStore(() => {
    productUpdatesList = buildProductUpdatesList();
    renderPage(currentPage);
  });

  // Attach navigation listeners
  container.querySelectorAll('[data-route]').forEach(link => {
    link.addEventListener('click', (e) => {
      e.preventDefault();
      if (onNavigate) onNavigate(link.getAttribute('data-route'));
    });
  });

  return container;
}
